import { Trans, t } from '@lingui/macro';
import { LogOut, Star, EyeOff, BellOff, PhoneOff, MailX, Bell } from 'lucide-react';
import { useEffect, useState } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { Room } from "../../models/Room";
import { RoomSetting } from "../../models/User";
import { Workspace } from "../../models/Workspace";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

import { cn } from "../../lib/utils";

interface IRoomOptionModal {
  room: Room;
  workspace: Workspace | undefined;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MyRoomOptionModal: React.FC<IRoomOptionModal> = ({ room, workspace, isOpen, onOpenChange }) => {
  const [roomCopy, setRoomCopy] = useState<Room | null>(null);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const { toast } = useToast();
  const isCreator = roomCopy?.creatorId && roomCopy.creatorId == My.user?.id;
  const history = useHistory();
  const roomName = roomCopy ? Room.getRoomNameWithSharp(roomCopy, My.user.id) : '';

  function onPresent() {
    setRoomCopy(Util.pojo(room));
  }

  async function leaveRoom() {
    onOpenChange(false);
    if (roomCopy) {
      setTimeout(async () => {
        Fire.leaveRoom(roomCopy.id, My.user);
        navigateSomewhereElse();
      }, 500);
      toast({
        title: t`You left ${roomCopy.name}`,
        duration: 2000,
      });
    }
  }

  function navigateSomewhereElse() {
    let otherRooms = Array.from(My.Rooms.values()).filter(r => r.id != roomCopy?.id);
    if (otherRooms.length > 0) {
      history.replace('/room/' + otherRooms[0].id);
    } else {
      history.replace('/welcome');
    }
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]" onOpenAutoFocus={onPresent}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {roomName && (
                <>
                  <span>{roomName}</span>
                  <span className="text-gray-400">·</span>
                </>
              )}
              <Trans>My Settings</Trans>
            </DialogTitle>
          </DialogHeader>

          {roomCopy && (
            <div className="space-y-4 py-4">
              <PersonalRoomSettings roomId={roomCopy.id} onSave={() => onOpenChange(false)} />

              {(!isCreator || roomCopy.isOneToOne) && (
                <div className="pt-4 border-t">
                  <Button
                    variant="outline"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setShowLeaveDialog(true)}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <Trans>Leave this room</Trans>
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Leave Room Confirmation Dialog */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Do you want to leave #{roomCopy?.name}?</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>You will no longer receive messages from this room.</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
            <AlertDialogAction
              onClick={leaveRoom}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trans>Leave</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MyRoomOptionModal;

const PersonalRoomSettings: React.FC<{ roomId: string; onSave: () => void }> = ({ roomId, onSave }) => {
  const [settings, setSettings] = useState<RoomSetting>(new RoomSetting('placeholder'));
  const { toast } = useToast();
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    let sett = My.RoomSettings.get(roomId);
    if (!sett) {
      sett = new RoomSetting(roomId);
      if (My.user.notificationSettings?.find(n => n.roomId == roomId && n.option === 'disable-push-notification')) {
        sett.disablePushNotifications = true;
      }
    }
    setSettings(Util.pojo(sett));
  }, [roomId]);

  function toggleSetting(key: keyof RoomSetting) {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanged(true);
  }

  function saveChanges() {
    toast({
      title: t`Saved changes`,
      duration: 2000,
    });
    My.saveRoomNotificationSetting(roomId, settings);
    setHasChanged(false);
    onSave();
  }

  /* ... inside PersonalRoomSettings component ... */
  const settingItems = [
    {
      key: 'favoriteRoomInMyList' as keyof RoomSetting,
      label: <Trans>Add to favorites</Trans>,
      icon: Star,
      checked: settings.favoriteRoomInMyList,
      color: "text-amber-600",
      bgColor: "bg-amber-100",
    },
    {
      key: 'hideRoomInMyList' as keyof RoomSetting,
      label: <Trans>Hide in My Rooms list</Trans>,
      icon: EyeOff,
      checked: settings.hideRoomInMyList,
      color: "text-slate-600",
      bgColor: "bg-slate-100",
    },
    {
      key: 'disablePushNotifications' as keyof RoomSetting,
      label: <Trans>Disable push notifications</Trans>,
      icon: BellOff,
      checked: settings.disablePushNotifications,
      color: "text-rose-600",
      bgColor: "bg-rose-100",
    },
    {
      key: 'allowMentionPushNotifications' as keyof RoomSetting,
      label: <Trans>Receive notifications when mentioned</Trans>,
      icon: Bell,
      checked: settings.allowMentionPushNotifications,
      color: "text-rose-600",
      bgColor: "bg-white",
      indent: true,
      hidden: !settings.disablePushNotifications
    },
    {
      key: 'disableVoiceCallPopups' as keyof RoomSetting,
      label: <Trans>Disable voice call popups</Trans>,
      icon: PhoneOff,
      checked: settings.disableVoiceCallPopups,
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
    },
    {
      key: 'disableEmailReminders' as keyof RoomSetting,
      label: <Trans>Disable email reminders</Trans>,
      icon: MailX,
      checked: settings.disableEmailReminders,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    }
  ];

  return (
    <div className="space-y-4">
      <div className="border rounded-xl divide-y bg-white overflow-hidden shadow-sm">
        {settingItems.map((item: any) => {
          if (item.hidden) return null;
          const Icon = item.icon;
          const isIndented = item.indent;

          return (
            <div
              key={item.key}
              className={cn(
                "flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors cursor-pointer group",
                isIndented && "pl-12 bg-gray-50/50"
              )}
              onClick={() => toggleSetting(item.key)}
            >
              <div className={cn("p-2 rounded-lg flex-shrink-0 transition-colors", item.bgColor)}>
                <Icon className={cn("h-5 w-5", item.color)} />
              </div>
              <div className="flex-1 min-w-0">
                <span className={cn(
                  "text-sm font-medium transition-colors",
                  item.checked ? "text-gray-900" : "text-gray-600"
                )}>
                  {item.label}
                </span>
              </div>
              <Checkbox
                id={item.key}
                checked={item.checked}
                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary pointer-events-none"
              />
            </div>
          );
        })}
      </div>

      <div className="flex justify-end pt-2">
        <Button
          onClick={saveChanges}
          disabled={!hasChanged}
          className="w-full sm:w-auto"
        >
          <Trans>Save changes</Trans>
        </Button>
      </div>
    </div>
  );
};